import { n8nProxyService } from "./n8nProxyService";

// Definir interfaces para as estruturas de dados
export interface N8nWorkflow {
  id: string | number;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  versionId?: number;
  nodes?: any[];
  connections?: any;
  staticData?: any;
  settings?: any;
  pinData?: any;
}

export interface WorkflowUpdateOptions {
  active?: boolean;
  name?: string;
  // Podemos adicionar mais campos conforme necessário para futuras operações
}

/**
 * Serviço especializado para operações relacionadas a workflows no n8n
 * Centraliza todas as operações de CRUD para workflows, tornando o código mais organizado e seguro
 */
class N8nWorkflowsService {
  /**
   * Buscar todos os workflows
   * @param options Opções adicionais de consulta
   */
  async getWorkflows(options: { 
    forceRefresh?: boolean, 
    active?: boolean,
    tags?: string[]
  } = {}): Promise<N8nWorkflow[]> {
    console.log(`[N8nWorkflowsService] Buscando workflows com opções:`, options);
    
    const { forceRefresh = true, active, tags } = options;
    
    // Preparar query params
    const queryParams: Record<string, string> = {};
    
    if (active !== undefined) {
      queryParams.active = active.toString();
    }
    
    if (tags && tags.length > 0) {
      queryParams.tags = tags.join(',');
    }
    
    try {
      // Usar API do n8nProxyService para carregar workflows
      const response = await n8nProxyService.callN8nApi<any>('workflows', {
        method: 'GET',
        useCache: !forceRefresh,
        queryParams
      });
      
      // Processar a resposta para garantir formato consistente
      return this.processWorkflowsResponse(response);
    } catch (error: any) {
      console.error("[N8nWorkflowsService] Erro ao buscar workflows:", error);
      throw new Error(`Erro ao buscar workflows: ${error.message || "Erro desconhecido"}`);
    }
  }
  
  /**
   * Processar a resposta da API para garantir formato consistente
   * Lida com diferentes formatos de resposta (array direto ou dentro de objeto 'data')
   */
  private processWorkflowsResponse(response: any): N8nWorkflow[] {
    let workflowsData: any[] = [];
    
    if (Array.isArray(response)) {
      console.log('[N8nWorkflowsService] Resposta recebida como array direto');
      workflowsData = response;
    } else if (response && typeof response === 'object') {
      if ('data' in response && Array.isArray(response.data)) {
        console.log('[N8nWorkflowsService] Resposta recebida como objeto com array "data"');
        workflowsData = response.data;
      } else {
        console.log('[N8nWorkflowsService] Resposta recebida como objeto singular, convertendo para array');
        // Se for apenas um objeto (e não um array), colocamos em um array
        workflowsData = [response];
      }
    }
    
    // Log detalhado para diagnóstico
    console.log(`[N8nWorkflowsService] Encontrados ${workflowsData.length} workflows`);
    if (workflowsData.length > 0) {
      console.log('[N8nWorkflowsService] Primeiro workflow:', JSON.stringify(workflowsData[0]).substring(0, 200) + '...');
    }
    
    // Normalizar os dados para garantir consistência
    return workflowsData.map(workflow => ({
      id: workflow.id,
      name: workflow.name || 'Sem nome',
      active: workflow.active === true, // Garantir que é booleano
      createdAt: workflow.createdAt || workflow.created_at || new Date().toISOString(),
      updatedAt: workflow.updatedAt || workflow.updated_at || new Date().toISOString(),
      versionId: workflow.versionId || workflow.version_id,
      // Outros campos podem ser adicionados conforme necessário
    }));
  }
  
  /**
   * Buscar um workflow específico por ID
   */
  async getWorkflow(id: string | number): Promise<N8nWorkflow> {
    console.log(`[N8nWorkflowsService] Buscando workflow com ID: ${id}`);
    
    try {
      const response = await n8nProxyService.callN8nApi<any>(`workflows/${id}`, {
        method: 'GET'
      });
      
      // Processar a resposta para garantir formato consistente
      const workflows = this.processWorkflowsResponse(response);
      return workflows[0];
    } catch (error: any) {
      console.error(`[N8nWorkflowsService] Erro ao buscar workflow ${id}:`, error);
      throw new Error(`Erro ao buscar workflow: ${error.message || "Erro desconhecido"}`);
    }
  }
  
  /**
   * Atualizar dados de um workflow
   * Permite atualizar vários aspectos do workflow, incluindo ativação/desativação
   */
  async updateWorkflow(id: string | number, options: WorkflowUpdateOptions): Promise<N8nWorkflow> {
    console.log(`[N8nWorkflowsService] Atualizando workflow ${id} com opções:`, options);
    
    try {
      // Primeiro, vamos obter o workflow atual usando a API diretamente para garantir todos os campos
      console.log(`[N8nWorkflowsService] Buscando dados completos do workflow ${id}`);
      const rawWorkflow = await n8nProxyService.callN8nApi<any>(`workflows/${id}`, {
        method: 'GET',
        useCache: false // Não usar cache para garantir dados atualizados
      });
      
      console.log(`[N8nWorkflowsService] Workflow obtido, verificando campos obrigatórios`);
      
      // Vamos verificar se temos os campos obrigatórios no workflow retornado
      if (!rawWorkflow || typeof rawWorkflow !== 'object') {
        throw new Error('Resposta inválida da API ao buscar workflow');
      }
      
      // Log para debug
      console.log(`[N8nWorkflowsService] Estrutura do workflow:`, {
        temName: 'name' in rawWorkflow,
        temNodes: 'nodes' in rawWorkflow,
        temConnections: 'connections' in rawWorkflow,
        temSettings: 'settings' in rawWorkflow
      });
      
      // Preparar o objeto para a requisição PUT
      // Precisamos garantir que todos os campos obrigatórios estejam presentes
      const requiredFields = ['name', 'nodes', 'connections', 'settings'];
      const missingFields = requiredFields.filter(field => !(field in rawWorkflow));
      
      if (missingFields.length > 0) {
        console.error(`[N8nWorkflowsService] Campos obrigatórios ausentes: ${missingFields.join(', ')}`);
        throw new Error(`Workflow retornado pela API está faltando campos obrigatórios: ${missingFields.join(', ')}`);
      }
      
      // Mesclar as opções atualizadas com o workflow atual
      // Importante: precisamos preservar a estrutura original completa
      const updatedWorkflow = {
        name: rawWorkflow.name,
        nodes: rawWorkflow.nodes || [],
        connections: rawWorkflow.connections || {},
        settings: rawWorkflow.settings || {},
        // Adicionar campos opcionais se existirem
        ...(rawWorkflow.staticData !== undefined ? { staticData: rawWorkflow.staticData } : {}),
        // Sobrescrever com as opções fornecidas
        ...options
      };
      
      console.log(`[N8nWorkflowsService] Enviando atualização com PUT, campos incluídos:`, Object.keys(updatedWorkflow));
      
      // Usar PUT, conforme documentação do n8n
      const response = await n8nProxyService.callN8nApi<any>(`workflows/${id}`, {
        method: 'PUT',
        body: updatedWorkflow
      });
      
      // Processar a resposta para garantir formato consistente
      const workflows = this.processWorkflowsResponse(response);
      return workflows[0];
    } catch (error: any) {
      console.error(`[N8nWorkflowsService] Erro ao atualizar workflow ${id}:`, error);
      throw new Error(`Erro ao atualizar workflow: ${error.message || "Erro desconhecido"}`);
    }
  }
  
  /**
   * Método especializado para ativar ou desativar um workflow
   * Foca na operação específica de alteração de status
   */
  async toggleWorkflowActive(id: string | number, active: boolean): Promise<N8nWorkflow> {
    console.log(`[N8nWorkflowsService] ${active ? 'Ativando' : 'Desativando'} workflow ${id}`);
    
    try {
      // Usar o endpoint específico para ativar/desativar
      const endpoint = active 
        ? `workflows/${id}/activate` 
        : `workflows/${id}/deactivate`;
      
      console.log(`[N8nWorkflowsService] Usando endpoint específico: ${endpoint}`);
      
      try {
        // Fazer uma requisição POST sem corpo para o endpoint específico
        const response = await n8nProxyService.callN8nApi<any>(endpoint, {
          method: 'POST',
          useCache: false
        });
        
        // Processar a resposta para garantir formato consistente
        const workflows = this.processWorkflowsResponse(response);
        return workflows[0];
      } catch (specificEndpointError) {
        // Mecanismo de fallback - se o endpoint específico falhar, tentamos o método genérico
        console.warn(`[N8nWorkflowsService] Endpoint específico falhou: ${specificEndpointError.message}. Usando método alternativo.`);
        
        // Tentar usar o método de atualização geral como fallback
        return await this.updateWorkflow(id, { active });
      }
    } catch (error: any) {
      console.error(`[N8nWorkflowsService] Erro ao ${active ? 'ativar' : 'desativar'} workflow ${id}:`, error);
      throw new Error(`Erro ao ${active ? 'ativar' : 'desativar'} workflow: ${error.message || "Erro desconhecido"}`);
    }
  }

  /**
   * Gerar a URL para visualizar um workflow no editor do n8n
   * @param workflowId ID do workflow a ser visualizado
   * @returns Promise com a URL completa para acesso ao workflow
   */
  async getWorkflowEditorUrl(workflowId: string | number): Promise<string> {
    console.log(`[N8nWorkflowsService] Gerando URL para visualizar workflow ${workflowId}`);
    
    try {
      // Obter a configuração do n8n
      const config = await n8nProxyService.getN8nConfig();
      
      if (!config.apiUrl) {
        throw new Error("URL da API do n8n não encontrada nas configurações");
      }
      
      // Construir a URL do editor de workflow
      // A URL do editor é diferente da API - é /workflow/{id} (singular) ao invés de /api/v1/workflows/{id}
      let editorUrl = config.apiUrl;
      editorUrl = editorUrl.endsWith('/') ? editorUrl : `${editorUrl}/`;
      editorUrl += `workflow/${workflowId}`;
      
      return editorUrl;
    } catch (error: any) {
      console.error(`[N8nWorkflowsService] Erro ao gerar URL para workflow ${workflowId}:`, error);
      throw new Error(`Erro ao gerar URL para workflow: ${error.message || "Erro desconhecido"}`);
    }
  }
}

// Exportar instância singleton do serviço
export const n8nWorkflowsService = new N8nWorkflowsService(); 